// API configuration and helper functions
import { signOut } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://8c2wmymtz6.execute-api.us-east-1.amazonaws.com/prod/v1';

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Helper function to handle API responses and check for unauthorized (401) responses
const handleApiResponse = async (response) => {
  // If token is invalid (401), clear it and reload to logout user
  if (response.status === 401) {
    console.warn('Token is invalid or expired. Logging out user.');
    clearLocalStorageToken();
    // Reload page to show login screen
    window.location.reload();
    return null;
  }
  
  return response;
};

// Helper function to decode JWT token and get the payload
const decodeToken = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  // exp is in seconds, convert to milliseconds and compare with current time
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
};

// Helper function to clear expired token and logout user
const clearLocalStorageToken = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_initial');
  // Regex for Cognito Identity Service Provider
  const cognitoKeyRegex = /^CognitoIdentityServiceProvider\./;

  Object.keys(localStorage)
    .filter(key => cognitoKeyRegex.test(key))
    .forEach(key => localStorage.removeItem(key));
};

// Helper function to check if user has a specific role
export const hasRole = (role) => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    return false;
  }
  
  const decoded = decodeToken(token);
  if (!decoded) {
    return false;
  }
  
  // Check AWS Cognito cognito:groups array for the role
  if (Array.isArray(decoded['cognito:groups']) && decoded['cognito:groups'].includes(role)) {
    return true;
  }
  
  return false;
};

export const isAdmin = () => {
  return hasRole('admin');
};

// Static list of all 32 teams
export const FOOTBALL_TEAMS = [
  'Cardinals',
  'Falcons',
  'Ravens',
  'Bills',
  'Panthers',
  'Bears',
  'Bengals',
  'Browns',
  'Cowboys',
  'Broncos',
  'Lions',
  'Packers',
  'Texans',
  'Colts',
  'Jaguars',
  'Chiefs',
  'Chargers',
  'Rams',
  'Saints',
  'Giants',
  'Jets',
  'Eagles',
  'Steelers',
  '49ers',
  'Seahawks',
  'Buccaneers',
  'Titans',
  'Vikings',
  'Commanders',
  'Patriots',
  'Dolphins',
  'Raiders'
];

const pendingReads = new Map();

// Only coalesce active reads. Later calls must still fetch edits and results.
const fetchSharedRead = (url) => {
  const headers = getAuthHeaders();
  const key = JSON.stringify([url, headers]);
  if (!pendingReads.has(key)) {
    const request = (async () => {
      const response = await handleApiResponse(await fetch(url, { headers }));
      if (!response) return null;
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return response.json();
    })().finally(() => pendingReads.delete(key));
    pendingReads.set(key, request);
  }
  return pendingReads.get(key);
};

export const fetchPicksData = async (weekNumber, seasonYear) => {
  try {
    const url = `${API_BASE_URL}/weekly-picks/week?week_number=${weekNumber}&season_year=${seasonYear}`;
    localStorage.setItem('current_week', weekNumber);
    return await fetchSharedRead(url);
  } catch (error) {
    console.error('Error fetching picks data:', error);
    throw error;
  }
};

export const fetchAllParticipants = async () => {
  try {
    const url = `${API_BASE_URL}/users`;
    return await fetchSharedRead(url);
  } catch (error) {
    console.error('Error fetching participants:', error);
    throw error;
  }
};

// Transform raw API data into the table structure
// New API format: Array of user objects with nested picks array
// { id, username, week_number, nfl_season_year, picks: [{line_number, pick, winner, locked}], week_tie_breaker, dns, dis, total }
// Table format: { [participant]: { [line_number]: { pick, winner, locked, notes } } }
export const transformPicksData = (rawData) => {
  const picksObject = {};

  rawData.forEach((userEntry) => {
    // eslint-disable-next-line no-unused-vars
    const { username, picks, dns, dis, total, week_tie_breaker } = userEntry;
    const displayName = username; // Use username which has full name with initial

    if (!picksObject[displayName]) {
      picksObject[displayName] = {};
    }

    // Process each pick in the nested picks array
    if (picks && picks.length > 0) {
      picks.forEach((pickEntry) => {
        const { line_number, pick, winner, locked } = pickEntry;
        
        picksObject[displayName][line_number] = {
          pick: pick || '',  // The team name from API
          winner: winner,  // true/false - whether the pick won
          locked: locked || false,  // true/false - whether the cell is locked/editable
          notes: ''
        };
      });
    }

    // Add Tie entry if week_tie_breaker exists
    if (week_tie_breaker !== undefined && week_tie_breaker !== null) {
      picksObject[displayName]['Tie'] = {
        pick: week_tie_breaker.toString(),
        winner: null,
        locked: false,
        notes: ''
      };
    }

    // Add Total entry with the total value from API
    picksObject[displayName]['Total'] = {
      total: total,  // The total value from the API
      notes: ''
    };
  });

  return picksObject;
};

export const saveAllPicks = async (participants, autocompleteValue, weeklyPicks, participantUserIds, currentWeek, currentYear) => {
  try {
      // Build payload with all participants
      const payload = [];

      for (const participant of participants) {
        const userId = participantUserIds[participant];
        const username = participant;

        if (!userId) {
          console.warn(`User ID not found for ${participant}`);
          continue;
        }

        // Get all keys for this participant from autocompleteValue
        const participantKeys = Object.keys(autocompleteValue).filter(key => 
          key.startsWith(`${participant}-`)
        );

        const picks = [];
        let tieValue = null;

        participantKeys.forEach(key => {
          const [, line] = key.split('-');
          const pickValue = autocompleteValue[key] || '';

          if (line === 'Tie') {
            tieValue = pickValue;
          } else if (pickValue) {
            const lineNum = parseInt(line);
            if (!isNaN(lineNum)) {
              picks.push({
                line_number: lineNum,
                pick: pickValue,
                winner: weeklyPicks[participant]?.[line]?.winner,
                locked: weeklyPicks[participant]?.[line]?.locked || false
              });
            }
          }
        });

        payload.push({
          user_id: userId,
          username: username,
          week_number: currentWeek,
          season_year: currentYear,
          picks: picks,
          week_tie_breaker: tieValue ? parseInt(tieValue) : null,
          dns: weeklyPicks[participant]?.['Tie']?.dns || false,
          dis: weeklyPicks[participant]?.['Tie']?.dis || false
        });
      }

      const url = `${API_BASE_URL}/weekly-picks`;
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        throw new Error('Token expired. Please log in again.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
      }

       // Handle 201 Created and 200 OK responses
      let data;
      if (response.status === 204) {
        // 204 No Content - no response body
        data = { success: true };
      } else {
        data = await response.json().catch(() => ({ success: true }));
      }
    
      return data;
    }
      catch (err) {
      console.error('Failed to save picks:', err);
    }
}

export const logout = async () => {
  // Remove the access token and all aws login stuff from localStorage
  clearLocalStorageToken();

  // Sign out from AWS Amplify/Cognito
  try {
    await signOut();
  } catch (error) {
    console.error('Error signing out from Cognito:', error);
  }
};

export const getAccessToken = () => {
  const token = localStorage.getItem('access_token');
  
  // Check if token is expired and clear it if so
  if (token && isTokenExpired(token)) {
    clearLocalStorageToken();
    return null;
  }
  
  return token;
};

export const getUserInitial = () => {
  return localStorage.getItem('user_initial');
};

export const getUsername = () => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    return null;
  }
  
  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }
  
  return decoded.username || null;
};

export const createUser = async (username) => {
  try {
    // Check if user has admin role
    if (!isAdmin()) {
      throw new Error('Only administrators can create users');
    }

    const url = `${API_BASE_URL}/users`;
    let response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        username: username,
      }),
    });

    response = await handleApiResponse(response);
    if (!response) return null;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    // Check if user has admin role
    if (!isAdmin()) {
      throw new Error('Only administrators can delete users');
    }

    const url = `${API_BASE_URL}/users/${userId}`;
    let response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    response = await handleApiResponse(response);
    if (!response) return null;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const updateWeekResults = async (weekNumber, seasonYear) => {
  try {
    // Check if user has admin role
    if (!isAdmin()) {
      throw new Error('Only administrators can update week results');
    }

    const url = `${API_BASE_URL}/weekly-picks/week?week_number=${weekNumber}&season_year=${seasonYear}`;
    let response = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });

    response = await handleApiResponse(response);
    if (!response) return null;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json().catch(() => ({ success: true }));
    return data;
  } catch (error) {
    console.error('Error updating week results:', error);
    throw error;
  }
};

export const saveUserPicksWithLink = async (userId, username, autocompleteValue, weeklyPicks, currentWeek, currentYear) => {
  try {
    const picks = [];
    let tieValue = null;

    // Extract picks from autocompleteValue
    Object.entries(autocompleteValue).forEach(([key, pickValue]) => {
      const [, line] = key.split('-');
      
      if (line === 'Tie') {
        tieValue = pickValue;
      } else if (pickValue) {
        const lineNum = parseInt(line);
        if (!isNaN(lineNum)) {
          picks.push({
            line_number: lineNum,
            pick: pickValue,
            winner: weeklyPicks[line]?.winner,
            locked: weeklyPicks[line]?.locked || false
          });
        }
      }
    });

    const payload = [{
      user_id: userId,
      username: username,
      week_number: currentWeek,
      season_year: currentYear,
      picks: picks,
      week_tie_breaker: tieValue ? parseInt(tieValue) : null,
      dns: weeklyPicks['Tie']?.dns || false,
      dis: weeklyPicks['Tie']?.dis || false
    }];

    const url = `${API_BASE_URL}/weekly-picks/user/${userId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      throw new Error('Invalid link or access denied.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    // Handle 204 No Content and 200 OK responses
    let data;
    if (response.status === 204) {
      data = { success: true };
    } else {
      data = await response.json().catch(() => ({ success: true }));
    }

    return data;
  } catch (err) {
    console.error('Failed to save user picks:', err);
    throw err;
  }
};
